<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="dashboard-title">Informe Diario de Facturación Electrónica</h1>
            <p class="text-caption grey--text">
              Comprobante oficial de ventas y recaudos del día
            </p>
          </div>
          <div class="d-flex align-center gap-2 mt-2 mt-sm-0">
            <v-text-field
              v-model="issueDate"
              label="Fecha del reporte"
              outlined
              dense
              type="date"
              hide-details
              class="mr-2"
              style="max-width: 200px"
            />
            <v-btn color="primary" @click="get_docs" :loading="loading_status">
              <v-icon left>mdi-magnify</v-icon>
              Buscar
            </v-btn>
            <v-btn color="success" @click="generatePDF" :disabled="!hasData">
              <v-icon left>mdi-file-pdf-box</v-icon>
              <span class="d-none d-sm-inline">Exportar PDF</span>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6" v-if="hasData">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex success-gradient">
              <v-icon color="white">mdi-cash-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Venta Total</div>
              <div class="kpi-value">{{ formatCurrency(totalSales) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="success">mdi-receipt</v-icon>
                {{ totalInvoices }} facturas
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex primary-gradient">
              <v-icon color="white">mdi-percent</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Impuestos</div>
              <div class="kpi-value">{{ formatCurrency(totalTaxes) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">IVA recaudado</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex warning-gradient">
              <v-icon color="white">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Productos Vendidos</div>
              <div class="kpi-value">{{ formatNumber(totalQuantity) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Unidades totales</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex info-gradient">
              <v-icon color="white">mdi-credit-card</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Método Principal</div>
              <div class="kpi-value text-truncate">{{ topPaymentMethod }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ topPaymentPercentage }}% del total
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <div ref="printArea">
      <!-- Store Information -->
      <v-card
        class="main-card elevation-0 mb-4"
        outlined
        v-if="seller.PartyIdentification"
      >
        <v-card-title class="pa-0">
          <v-toolbar flat class="minimal-toolbar">
            <v-icon class="mr-2" color="primary">mdi-store</v-icon>
            <h3 class="card-title">Información del Establecimiento</h3>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-simple-table class="info-table">
            <tbody>
              <tr>
                <td class="info-label">Razón Social</td>
                <td class="font-weight-medium">{{ seller.PartyName }}</td>
                <td class="info-label">NIT</td>
                <td class="font-weight-medium">{{ seller.PartyIdentification }}</td>
              </tr>
              <tr>
                <td class="info-label">Dirección</td>
                <td>{{ seller.AddressLine }}</td>
                <td class="info-label">Ciudad</td>
                <td>{{ seller.CityName }}</td>
              </tr>
              <tr>
                <td class="info-label">Email</td>
                <td>{{ seller.client_email }}</td>
                <td class="info-label">Teléfono</td>
                <td>{{ seller.client_phone }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Resolutions -->
      <v-card class="main-card elevation-0 mb-4" outlined v-if="resolutions.length > 0">
        <v-card-title class="pa-0">
          <v-toolbar flat class="minimal-toolbar">
            <v-icon class="mr-2" color="success">mdi-certificate</v-icon>
            <h3 class="card-title">Resolución de Facturación Electrónica</h3>
          </v-toolbar>
        </v-card-title>

        <v-data-table
          :headers="header_resolution"
          :items="resolutions"
          :items-per-page="-1"
          hide-default-footer
          class="minimal-table"
          dense
        >
          <template v-slot:[`item.InvoiceAuthorization`]="{ item }">
            <v-chip small label color="success" outlined>
              {{ item.InvoiceAuthorization }}
            </v-chip>
          </template>

          <template v-slot:[`item.Prefix`]="{ item }">
            <span class="font-weight-medium">{{ item.Prefix }}</span>
          </template>
        </v-data-table>
      </v-card>

      <!-- Payment Methods -->
      <v-card class="main-card elevation-0 mb-4" outlined>
        <v-card-title class="pa-0">
          <v-toolbar flat class="minimal-toolbar">
            <v-icon class="mr-2" color="info">mdi-cash-register</v-icon>
            <h3 class="card-title">Recaudos de Venta</h3>
            <v-spacer></v-spacer>
            <v-chip small label> {{ payments.length }} métodos </v-chip>
          </v-toolbar>
        </v-card-title>

        <v-data-table
          :headers="header_payments"
          :items="payments"
          :items-per-page="-1"
          hide-default-footer
          class="minimal-table"
          :loading="loading_status"
          dense
        >
          <template v-slot:[`item.type`]="{ item }">
            <div class="payment-method">
              <v-icon small class="mr-2" :color="getPaymentColor(item.type)">
                {{ getPaymentIcon(item.type) }}
              </v-icon>
              {{ item.type }}
            </div>
          </template>

          <template v-slot:[`item.quantity`]="{ item }">
            <v-chip x-small label>
              {{ formatNumber(item.quantity) }}
            </v-chip>
          </template>

          <template v-slot:[`item.value`]="{ item }">
            <div class="text-right">
              <div class="font-weight-medium">{{ formatCurrency(item.value) }}</div>
              <div class="text-caption grey--text">
                {{ getPercentage(item.value, getTotal(payments, "value")) }}%
              </div>
            </div>
          </template>

          <template v-slot:body.append>
            <tr class="grey lighten-4 font-weight-bold">
              <td>TOTAL</td>
              <td class="text-center">{{ getTotalFormatted(payments, "quantity") }}</td>
              <td class="text-right">
                {{ formatCurrency(getTotal(payments, "value")) }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <!-- Sales by Category -->
      <v-card class="main-card elevation-0 mb-4" outlined>
        <v-card-title class="pa-0">
          <v-toolbar flat class="minimal-toolbar">
            <v-icon class="mr-2" color="warning">mdi-chart-bar</v-icon>
            <h3 class="card-title">Discriminación de Ventas por Departamento</h3>
            <v-spacer></v-spacer>
            <v-chip small label> {{ sales.length }} categorías </v-chip>
          </v-toolbar>
        </v-card-title>

        <v-data-table
          :headers="header_sales"
          :items="sales"
          :items-per-page="-1"
          hide-default-footer
          class="minimal-table"
          :loading="loading_status"
          dense
        >
          <template v-slot:[`item.category`]="{ item }">
            <div class="category-cell">
              <v-avatar
                size="8"
                :color="getCategoryColor(item.category)"
                class="mr-2"
              ></v-avatar>
              {{ item.category }}
            </div>
          </template>

          <template v-slot:[`item.tax_rate`]="{ item }">
            <v-chip x-small label :color="getTaxRateColor(item.tax_rate)">
              {{ item.tax_rate }}%
            </v-chip>
          </template>

          <template v-slot:[`item.quantity`]="{ item }">
            {{ formatNumber(item.quantity) }}
          </template>

          <template v-slot:[`item.taxes`]="{ item }">
            <span class="orange--text text--darken-2">{{
              formatCurrency(item.taxes)
            }}</span>
          </template>

          <template v-slot:[`item.price`]="{ item }">
            <span class="font-weight-medium">{{ formatCurrency(item.price) }}</span>
          </template>

          <template v-slot:[`item.total`]="{ item }">
            <span class="font-weight-bold primary--text">
              {{
                formatCurrency(parseFloat(item.price || 0) + parseFloat(item.taxes || 0))
              }}
            </span>
          </template>

          <template v-slot:body.append>
            <tr class="grey lighten-4 font-weight-bold">
              <td>TOTAL</td>
              <td class="text-center">{{ getTotalFormatted(sales, "quantity") }}</td>
              <td class="text-right orange--text text--darken-2">
                {{ formatCurrency(getTotal(sales, "taxes")) }}
              </td>
              <td class="text-center">-</td>
              <td class="text-right">{{ formatCurrency(getTotal(sales, "price")) }}</td>
              <td class="text-right primary--text">
                {{ formatCurrency(getTotal(sales, "price") + getTotal(sales, "taxes")) }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <!-- Invoices by Tax Rate -->
      <v-card class="main-card elevation-0" outlined>
        <v-card-title class="pa-0">
          <v-toolbar flat class="minimal-toolbar">
            <v-icon class="mr-2" color="error">mdi-file-document-multiple</v-icon>
            <h3 class="card-title">Registros de Facturas por Tasa de Impuesto</h3>
            <v-spacer></v-spacer>
            <v-chip small label color="error" text-color="white">
              {{ uniqueInvoicesCount }} facturas
            </v-chip>
          </v-toolbar>
        </v-card-title>

        <v-data-table
          :headers="header_invoices"
          :items="invoices"
          :items-per-page="10"
          class="minimal-table"
          :loading="loading_status"
          dense
          :footer-props="{
            'items-per-page-options': [10, 25, 50, -1],
          }"
        >
          <template v-slot:[`item.invoice`]="{ item }">
            <div class="invoice-number">
              <v-icon small class="mr-1" color="primary">mdi-receipt</v-icon>
              <span class="font-weight-medium">{{ item.invoice }}</span>
            </div>
          </template>

          <template v-slot:[`item.tax_rate`]="{ item }">
            <v-chip x-small label :color="getTaxRateColor(item.tax_rate)">
              {{ item.tax_rate }}%
            </v-chip>
          </template>

          <template v-slot:[`item.subtotal`]="{ item }">
            {{ formatCurrency(item.subtotal) }}
          </template>

          <template v-slot:[`item.discount`]="{ item }">
            <span class="red--text">{{ formatCurrency(item.discount) }}</span>
          </template>

          <template v-slot:[`item.taxes`]="{ item }">
            <span class="orange--text text--darken-2">{{
              formatCurrency(item.taxes)
            }}</span>
          </template>

          <template v-slot:[`item.price`]="{ item }">
            <span class="font-weight-medium">{{ formatCurrency(item.price) }}</span>
          </template>

          <template v-slot:[`item.total`]="{ item }">
            <span class="font-weight-bold primary--text">{{
              formatCurrency(item.total)
            }}</span>
          </template>

          <template v-slot:[`item.bags`]="{ item }">
            <span v-if="shouldShowBags(item)" class="green--text">
              {{ formatCurrency(item.bags) }}
            </span>
            <span v-else class="grey--text">-</span>
          </template>

          <template v-slot:body.append>
            <tr class="grey lighten-4 font-weight-bold">
              <td>TOTAL</td>
              <td class="text-right">
                {{ formatCurrency(getTotal(invoices, "subtotal")) }}
              </td>
              <td class="text-right red--text">
                {{ formatCurrency(getTotal(invoices, "discount")) }}
              </td>
              <td class="text-right">
                {{ formatCurrency(getTotal(invoices, "price")) }}
              </td>
              <td class="text-right orange--text text--darken-2">
                {{ formatCurrency(getTotal(invoices, "taxes")) }}
              </td>
              <td class="text-center">-</td>
              <td class="text-right primary--text">
                {{ formatCurrency(getTotal(invoices, "total")) }}
              </td>
              <td class="text-right green--text">{{ formatCurrency(getTotalBags()) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Empty State -->
    <v-card v-if="!hasData && !loading_status" class="empty-state elevation-0" outlined>
      <v-card-text class="text-center py-12">
        <v-icon size="64" color="grey lighten-2">mdi-file-document-outline</v-icon>
        <h3 class="text-h6 mt-4 grey--text">No hay datos para mostrar</h3>
        <p class="grey--text mt-2">
          Selecciona una fecha y haz clic en "Buscar" para generar el informe
        </p>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "../../services/webserver.js";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "DailyReport",

  data() {
    return {
      // Loading state
      loading_status: false,

      // Date
      issueDate: getToday(),

      // Data
      payments: [],
      sales: [],
      invoices: [],
      processedInvoices: new Set(),
      resolutions: [],
      seller: {
        AddressLine: "",
        CityName: "",
        PartyIdentification: "",
        PartyIdentification_Id: "",
        PartyName: "",
        TaxLevelCode: "",
        client_email: "",
        client_phone: "",
        store_Caja: "",
        store_id: "",
        store_name: "",
      },

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Headers
      header_store: [
        {
          text: "Nombre",
          value: "PartyName",
          sortable: false,
        },
        {
          text: "Identificación",
          value: "PartyIdentification",
          align: "center",
        },
        {
          text: "Dirección",
          value: "AddressLine",
          align: "center",
        },
        {
          text: "Ciudad",
          value: "CityName",
          align: "center",
        },
        {
          text: "Email",
          value: "client_email",
          align: "center",
        },
        {
          text: "Teléfono",
          value: "client_phone",
          align: "center",
        },
      ],

      header_resolution: [
        {
          text: "Autorización",
          value: "InvoiceAuthorization",
          sortable: false,
          width: "25%",
        },
        {
          text: "Prefijo",
          value: "Prefix",
          align: "center",
          width: "10%",
        },
        {
          text: "Desde",
          value: "AuthorizationFrom",
          align: "center",
          width: "15%",
        },
        {
          text: "Hasta",
          value: "AuthorizationTo",
          align: "center",
          width: "15%",
        },
        {
          text: "Fecha Inicio",
          value: "StartDate",
          align: "center",
          width: "17.5%",
        },
        {
          text: "Fecha Fin",
          value: "EndDate",
          align: "center",
          width: "17.5%",
        },
      ],

      header_sales: [
        {
          text: "Categoría",
          value: "category",
          sortable: false,
          width: "30%",
        },
        {
          text: "Cantidad",
          value: "quantity",
          align: "center",
          width: "15%",
        },
        {
          text: "Impuesto",
          value: "taxes",
          align: "right",
          width: "15%",
        },
        {
          text: "Tasa",
          value: "tax_rate",
          align: "center",
          width: "10%",
        },
        {
          text: "Valor Base",
          value: "price",
          align: "right",
          width: "15%",
        },
        {
          text: "Total",
          value: "total",
          align: "right",
          width: "15%",
        },
      ],

      header_invoices: [
        {
          text: "Comprobante",
          value: "invoice",
          sortable: false,
          width: "20%",
        },
        {
          text: "Subtotal",
          value: "subtotal",
          align: "right",
          width: "12%",
        },
        {
          text: "Descuento",
          value: "discount",
          align: "right",
          width: "12%",
        },
        {
          text: "Base Gravable",
          value: "price",
          align: "right",
          width: "14%",
        },
        {
          text: "Impuesto",
          value: "taxes",
          align: "right",
          width: "12%",
        },
        {
          text: "Tasa",
          value: "tax_rate",
          align: "center",
          width: "8%",
        },
        {
          text: "Total",
          value: "total",
          align: "right",
          width: "14%",
        },
        {
          text: "Imp.Bolsas",
          value: "bags",
          align: "right",
          width: "8%",
        },
      ],

      header_payments: [
        {
          text: "Forma de Pago",
          value: "type",
          sortable: false,
          width: "50%",
        },
        {
          text: "Transacciones",
          value: "quantity",
          align: "center",
          width: "25%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "25%",
        },
      ],
    };
  },

  computed: {
    // Check if we have data
    hasData() {
      return (
        this.sales.length > 0 || this.invoices.length > 0 || this.payments.length > 0
      );
    },

    // KPI calculations
    totalSales() {
      return this.getTotal(this.invoices, "total");
    },

    totalTaxes() {
      return this.getTotal(this.invoices, "taxes");
    },

    totalQuantity() {
      return this.getTotal(this.sales, "quantity");
    },

    totalInvoices() {
      return new Set(this.invoices.map((i) => i.invoice)).size;
    },

    uniqueInvoicesCount() {
      return new Set(this.invoices.map((i) => i.invoice)).size;
    },

    topPaymentMethod() {
      if (this.payments.length === 0) return "-";
      const sorted = [...this.payments].sort((a, b) => b.value - a.value);
      return sorted[0].type;
    },

    topPaymentPercentage() {
      if (this.payments.length === 0) return 0;
      const total = this.getTotal(this.payments, "value");
      const sorted = [...this.payments].sort((a, b) => b.value - a.value);
      return total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    },
  },

  mounted() {
    this.get_docs();
  },

  methods: {
    // Formatting methods
    formatCurrency(value) {
      value = parseFloat(value || 0);
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    formatNumber(value) {
      return parseFloat(value || 0).toLocaleString("es-CO");
    },

    // Utility methods
    getTotal(items, key) {
      return items.reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);
    },

    getTotalFormatted(items, key) {
      const total = this.getTotal(items, key);
      return total.toLocaleString("es-CO");
    },

    getPercentage(value, total) {
      if (!total || total === 0) return 0;
      return Math.round((value / total) * 100);
    },

    getCategoryColor(category) {
      const colors = [
        "#1976D2",
        "#388E3C",
        "#F57C00",
        "#D32F2F",
        "#7B1FA2",
        "#00796B",
        "#5D4037",
        "#455A64",
      ];
      const index = Math.abs(category.charCodeAt(0) - 65) % colors.length;
      return colors[index];
    },

    getPaymentIcon(payment) {
      const icons = {
        EFECTIVO: "mdi-cash",
        TARJETA: "mdi-credit-card",
        TRANSFERENCIA: "mdi-bank-transfer",
        CREDITO: "mdi-calendar-clock",
        CHEQUE: "mdi-checkbook",
      };
      const key = Object.keys(icons).find((k) => payment.toUpperCase().includes(k));
      return icons[key] || "mdi-cash-multiple";
    },

    getPaymentColor(payment) {
      const colors = {
        EFECTIVO: "success",
        TARJETA: "primary",
        TRANSFERENCIA: "info",
        CREDITO: "warning",
        CHEQUE: "secondary",
      };
      const key = Object.keys(colors).find((k) => payment.toUpperCase().includes(k));
      return colors[key] || "grey";
    },

    getTaxRateColor(rate) {
      rate = parseFloat(rate);
      if (rate === 0) return "grey";
      if (rate <= 5) return "success";
      if (rate <= 10) return "warning";
      if (rate <= 15) return "orange";
      return "error";
    },

    // Show message
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Bags logic
    shouldShowBags(item) {
      if (!item.invoice || !item.bags || item.bags === 0) return false;

      if (this.processedInvoices.has(item.invoice)) {
        return false;
      }

      this.processedInvoices.add(item.invoice);
      return true;
    },

    getTotalBags() {
      const uniqueInvoices = new Set();
      let totalBags = 0;

      this.invoices.forEach((item) => {
        if (!uniqueInvoices.has(item.invoice) && item.bags) {
          totalBags += parseFloat(item.bags || 0);
          uniqueInvoices.add(item.invoice);
        }
      });

      return totalBags;
    },

    // Data loading
    get_docs() {
      const qry = {
        store: window.profile.store_id,
        store_id: window.profile.store_id,
        issueDate: this.issueDate,
        filters: [],
      };

      this.loading_status = true;
      this.processedInvoices = new Set();

      webserver(
        "sales_report_fe",
        qry,
        (data) => {
          this.loading_status = false;
          console.log("sales_report_fe", data);
          // Process sales data
          if (data.sales) {
            this.sales = data.sales.map((item) => ({
              ...item,
              quantity: parseFloat(item.quantity || 0),
              taxes: parseFloat(item.taxes || 0),
              price: parseFloat(item.price || 0),
              tax_rate: parseFloat(item.tax_rate || 0),
              total: parseFloat(item.price || 0) + parseFloat(item.taxes || 0),
            }));
          } else {
            this.sales = [];
          }

          // Process invoices data
          if (data.invoices) {
            this.invoices = data.invoices.map((item) => ({
              ...item,
              quantity: parseFloat(item.quantity || 0),
              subtotal: parseFloat(item.subtotal || 0),
              discount: parseFloat(item.discount || 0),
              price: parseFloat(item.price || 0),
              taxes: parseFloat(item.taxes || 0),
              total: parseFloat(item.total || 0),
              bags: parseFloat(item.bags || 0),
              tax_rate: parseFloat(item.tax_rate || 0),
            }));
          } else {
            this.invoices = [];
          }

          // Process payments data
          if (data.payments) {
            this.payments = data.payments.map((item) => ({
              ...item,
              quantity: parseFloat(item.quantity || 0),
              value: parseFloat(item.value || 0),
            }));
          } else {
            this.payments = [];
          }

          this.resolutions = data.resolutions || [];
          this.seller = data.seller || this.seller;

          this.showMessage("Informe cargado correctamente", "success");
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar el informe", "error");
          console.error(error);
        }
      );
    },

    // PDF generation
    reorderData(dataArray, order) {
      return dataArray.map((data) => {
        let reorderedData = {};
        order.forEach((key) => {
          reorderedData[key] = data[key];
        });
        return reorderedData;
      });
    },

    generatePDF() {
      try {
        // Prepare data for PDF
        var categorySales = [...this.sales];
        categorySales.forEach((item) => {
          item.taxes = parseFloat(item.taxes || 0);
          item.price = parseFloat(item.price || 0);
          item.quantity = parseFloat(item.quantity || 0);
          item.tax_rate = parseFloat(item.tax_rate || 0);
          item.tax_amount = ((item.price * item.tax_rate) / 100).toFixed(2);
          item.total = item.price + item.taxes;
        });

        // Add totals row
        categorySales.push({
          category: "Totales",
          tax_rate: "",
          quantity: this.getTotal(this.sales, "quantity"),
          taxes: this.getTotal(this.sales, "taxes"),
          price: this.getTotal(this.sales, "price"),
          tax_amount: "-",
          total: this.getTotal(this.sales, "price") + this.getTotal(this.sales, "taxes"),
        });

        var pagos = [...this.payments];
        pagos.forEach((item) => {
          item.value = parseFloat(item.value || 0);
          item.quantity = parseFloat(item.quantity || 0);
        });

        pagos.push({
          type: "Totales",
          quantity: this.getTotal(this.payments, "quantity"),
          value: this.getTotal(this.payments, "value"),
        });

        // Prepare invoices for PDF
        this.processedInvoices = new Set();

        var facturas = [...this.invoices];
        facturas.forEach((item) => {
          item.subtotal = parseFloat(item.subtotal || 0);
          item.discount = parseFloat(item.discount || 0);
          item.price = parseFloat(item.price || 0);
          item.taxes = parseFloat(item.taxes || 0);
          item.total = parseFloat(item.total || 0);
          item.bags = parseFloat(item.bags || 0);
          item.tax_rate = parseFloat(item.tax_rate || 0);
          item.tax_amount = ((item.price * item.tax_rate) / 100).toFixed(2);

          if (this.processedInvoices.has(item.invoice)) {
            item.bags = 0;
          } else {
            this.processedInvoices.add(item.invoice);
          }
        });

        var tableHeaders = this.header_invoices.map((header) => header.value);
        var ventas = this.reorderData(facturas, tableHeaders);

        // Add totals row for invoices
        ventas.push({
          invoice: "Totales",
          tax_rate: "",
          subtotal: this.getTotal(facturas, "subtotal"),
          discount: this.getTotal(facturas, "discount"),
          price: this.getTotal(facturas, "price"),
          taxes: this.getTotal(facturas, "taxes"),
          tax_amount: "-",
          total: this.getTotal(facturas, "total"),
          bags: this.getTotalBags(),
        });

        const pdf = new jsPDF("p", "mm", "a4");
        let currentY = 10;

        // Add title and date
        const reportTitle = "Comprobante de Informe Diario Facturación Electrónica";
        const reportDate = `Fecha del informe: ${this.issueDate}`;
        const printDate = `Fecha de impresión: ${new Date().toLocaleDateString("es-CO")}`;

        pdf.setFontSize(16);
        pdf.text(reportTitle, 10, 10);
        pdf.setFontSize(10);
        pdf.text(reportDate, 10, 20);
        pdf.text(printDate, 10, 25);
        currentY = 35;

        const tables = [
          {
            headers: this.header_store || [],
            items: [this.seller] || [],
            title: "Información del Establecimiento",
          },
          {
            headers: this.header_resolution || [],
            items: this.resolutions || [],
            title: "Resolución de Facturación Electrónica",
          },
          {
            headers: this.header_sales || [],
            items: categorySales || [],
            title: "Discriminación de Ventas por Departamento",
          },
          {
            headers: this.header_payments || [],
            items: pagos || [],
            title: "Recaudos de Venta",
          },
          {
            headers: this.header_invoices || [],
            items: ventas || [],
            title: "Registros de Facturas por Tasa de Impuesto",
          },
        ];

        tables.forEach((table, index) => {
          if (index > 0) {
            currentY += 10;
          }

          pdf.setFontSize(10);
          pdf.text(table.title, 10, currentY);

          const tableData = table.items.map((item) => {
            return Object.values(item).map((value) => {
              if (typeof value === "number") {
                return this.formatCurrency(value);
              } else if (value === null || value === undefined) {
                return "";
              } else {
                return value;
              }
            });
          });

          const tableHeaders = table.headers.map((header) => header.text);

          const numericColumns = [];
          if (table.items.length > 0) {
            Object.keys(table.items[0]).forEach((key, colIndex) => {
              if (typeof table.items[0][key] === "number") {
                numericColumns.push(colIndex);
              }
            });
          }

          const columnStyles = {};
          numericColumns.forEach((colIndex) => {
            columnStyles[colIndex] = { halign: "right" };
          });

          pdf.autoTable({
            startY: currentY + 5,
            head: [tableHeaders],
            body: tableData,
            theme: "grid",
            styles: { cellPadding: 2, fontSize: 8 },
            headStyles: { fontSize: 8, fillColor: [33, 150, 243] },
            footStyles: { fontSize: 8, fillColor: [224, 224, 224] },
            columnStyles: columnStyles,
            didDrawPage: (data) => {
              currentY = data.cursor.y;
            },
          });
        });

        const fileName = `informe_diario_${this.issueDate}.pdf`;
        pdf.save(fileName);

        this.showMessage("PDF generado correctamente", "success");
      } catch (error) {
        this.showMessage("Error al generar el PDF", "error");
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
/* Dashboard Title */
.dashboard-title {
  font-size: 2rem;
  font-weight: 300;
  color: #333;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 32px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Gradients */
.success-gradient {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
}

.primary-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}

.warning-gradient {
  background: linear-gradient(135deg, #fb8c00 0%, #ffa726 100%);
}

.info-gradient {
  background: linear-gradient(135deg, #00acc1 0%, #26c6da 100%);
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Info Table */
.info-table {
  border: none !important;
}

.info-table tbody tr td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

.info-label {
  font-weight: 500;
  color: #666;
  width: 20%;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

/* Custom cells */
.payment-method {
  display: flex;
  align-items: center;
}

.category-cell {
  display: flex;
  align-items: center;
}

.invoice-number {
  display: flex;
  align-items: center;
}

/* Empty State */
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Text utilities */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Gap utility */
.gap-2 {
  gap: 8px;
}

/* Responsive */
@media (max-width: 600px) {
  .dashboard-title {
    font-size: 1.5rem;
  }

  .kpi-value {
    font-size: 1.5rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    padding: 12px 16px !important;
  }

  .card-title {
    font-size: 1rem;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.625rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
